import { IMAGE_MODEL_CATALOG } from './imageModelCatalog';
import { VIDEO_MODEL_CATALOG, VideoModelOption } from './videoModelCatalog';

/**
 * Quantos vídeos ou imagens cabem na franquia, no modelo mais barato.
 * Usa a tarifa cobrada (já ≥ ceil(COGS × 1.15 / 0.01)), não o piso:
 * usos = floor(créditos / créditos_da_geração).
 * Vídeo = clip mínimo (cr/s × duração mínima) entre T2V/I2V.
 * Avatar fica de fora: cobra pela duração do áudio, não é um clipe comum.
 */
export type CheapestVideo = {
  label: string;
  resolution: string;
  seconds: number;
  creditsPerSecond: number;
  /** Clip mínimo: créditos/segundo × duração mínima. */
  credits: number;
};

export type CheapestImage = {
  label: string;
  credits: number;
};

function clipOf(model: VideoModelOption): CheapestVideo | null {
  if (model.requiresDrivingAudio) return null;
  if (!model.modalities.includes('t2v') && !model.modalities.includes('i2v')) return null;
  const resolution = model.resolutions.reduce((best, row) =>
    row.creditsPerSecond < best.creditsPerSecond ? row : best,
  );
  return {
    label: model.label,
    resolution: resolution.label,
    seconds: model.durationMin,
    creditsPerSecond: resolution.creditsPerSecond,
    credits: resolution.creditsPerSecond * model.durationMin,
  };
}

export function cheapestVideo(): CheapestVideo {
  const clips = VIDEO_MODEL_CATALOG.map(clipOf).filter((row): row is CheapestVideo => row !== null && row.credits > 0);
  return clips.reduce((best, row) => (row.credits < best.credits ? row : best));
}

export function cheapestImage(): CheapestImage {
  const row = IMAGE_MODEL_CATALOG.reduce((best, model) =>
    model.creditsPerImage < best.creditsPerImage ? model : best,
  );
  return { label: row.label, credits: row.creditsPerImage };
}

export function planMediaCounts(monthlyCredits: number): { videos: number; images: number } {
  const credits = Math.max(0, monthlyCredits);
  return {
    videos: Math.floor(credits / cheapestVideo().credits),
    images: Math.floor(credits / cheapestImage().credits),
  };
}

const pt = new Intl.NumberFormat('pt-BR');

/** Uma linha: a franquia inteira em vídeos ou em imagens, no modelo mais barato. */
export function planMediaLine(monthlyCredits: number): string {
  const video = cheapestVideo();
  const { videos, images } = planMediaCounts(monthlyCredits);
  return `Até ${pt.format(videos)} vídeos de ${video.seconds}s ou ${pt.format(images)} imagens`;
}

/** Recursos do produto nas caixas de plano (Explorar + ferramentas). */
export function planProductFeatures(readyModels: number | null): string[] {
  const lines: string[] = [];
  if (readyModels != null && readyModels > 0) {
    lines.push(
      `+${pt.format(readyModels)} ${readyModels === 1 ? 'modelo pronto de vídeo' : 'modelos prontos de vídeo'}`,
    );
  }
  lines.push(
    'Agente de IA totalmente conectado às ferramentas',
    'Editor com legendas, trilha e narração',
    'Mobiliação de ambientes com IA',
    'Galeria e kanban da produção',
  );
  return lines;
}
